import debounce from 'debounce'
import ApolloClient from 'apollo-client'
import * as Sentry from '@sentry/browser'

import {
  getActiveAuthorization,
  getAllAuthorizations,
  removeActiveAuthorization,
} from 'graphql/multisession'

import { routes } from '@sketch/constants'
import { signOut } from 'modules/user/useSignOut'
import { trackEvent } from 'modules/analytics'

import store from 'redux/store'
import { actions as toastActions } from '@sketch/toasts'
import type { History } from 'history'

export const debouncedLogoutActiveSession = debounce(
  (
    client: ApolloClient<object>,
    history: History,
    operationName: string,
    message: string,
    reason?: string
  ) => {
    const allAuthorizations = getAllAuthorizations()
    store.dispatch(toastActions.add({ message, variant: 'negative' }))

    const forceLogoutEventProps = reason
      ? { operationName, message, reason }
      : { operationName, message }

    trackEvent(
      'AUTH - invalid active authorization removed',
      forceLogoutEventProps
    )

    const activeAuthorization = getActiveAuthorization(client.cache)
    const sentryBreadcrumbData = {
      active: {
        type: activeAuthorization?.type,
        expirationDateTime: activeAuthorization?.fragment.expirationDateTime,
      },
      // Convert to string as arrays don't seem to be displayed on Sentry breadcrumbs UI
      all: JSON.stringify(
        allAuthorizations.map(auth => ({
          type: auth.type,
          expirationDateTime: auth.fragment.expirationDateTime,
        }))
      ),
    }
    if (allAuthorizations.length <= 1) {
      Sentry.addBreadcrumb({
        category: 'Authentication',
        message: `Force signout`,
        level: 'log',
        data: sentryBreadcrumbData,
      })
      trackEvent('AUTH - user forced to logout', forceLogoutEventProps)
      signOut({
        client,
        history,
        reason: reason || 'Forced to logout the only active session',
      })
    } else {
      Sentry.addBreadcrumb({
        category: 'Authentication',
        message: `Force auth token removal`,
        level: 'log',
        data: sentryBreadcrumbData,
      })
      removeActiveAuthorization(client.cache)
      history.push(routes.ENTRY.create({}))
    }
  },
  100,
  true
)
